import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useHits, useInstantSearch } from 'react-instantsearch';
import { useRouter } from 'next/router';
import cx from 'classnames';
import type { Hit as AlgoliaHitType } from 'instantsearch.js';

import styles from 'src/components/docs/Search/SearchResults.module.scss';
import DocsContext from 'src/contexts/docs';
import SearchHit from './SearchHit';

interface HitHierarchy {
  lvl0?: string;
  lvl1?: string;
  lvl2?: string;
  lvl3?: string;
  lvl4?: string;
  lvl5?: string;
}

interface HitFields {
  objectID: string;
  url: string;
  url_without_anchor?: string;
  anchor?: string;
  content?: string;
  type?: string;
  hierarchy?: HitHierarchy;
  __position?: number;
}

type Hit = AlgoliaHitType<HitFields>;

interface HitsProps {
  hits: Hit[];
  query: string;
  sendEvent: (eventType: string, hit: Hit, eventName: string) => void;
}

const EmptyState: React.FC = () => {
  return null;
};

const NoResults: React.FC<{ query: string }> = ({ query }) => {
  return (
    <div className={styles.noResults} role='alert'>
      <p className={styles.noResultsText}>
        <span className={styles.semibold}>
          Try searching for another word or phrase.
        </span>
        <br />
        No matches were found for{' '}
        <span className={styles.semibold}>{query}</span>
      </p>
    </div>
  );
};

interface HitsPropsWithTotal extends HitsProps {
  totalResults: number;
}

const Hits: React.FC<HitsPropsWithTotal> = ({
  hits,
  query,
  sendEvent,
  totalResults,
}) => {
  const [selectedElement, setSelectedElement] = useState(-1);
  const [selectedURL, setSelectedURL] = useState<string | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const router = useRouter();
  const { dispatch } = useContext(DocsContext);

  const closeSearch = useCallback(() => {
    dispatch({
      type: 'SET_SEARCH_VISIBILITY',
      payload: { visibility: false, openSearchClicked: false },
    });
    // Blur the search input after state update to move focus back to the page
    // Use setTimeout to ensure this runs after React re-renders
    setTimeout(() => {
      const searchInput = document.getElementById('search-input');
      if (searchInput instanceof HTMLElement) {
        searchInput.blur();
      }
    }, 0);
  }, [dispatch]);

  const onEvent = useCallback(
    (key: KeyboardEvent) => {
      switch (key.keyCode) {
        case 38: // up arrow
          setSelectedElement(Math.max(0, selectedElement - 1));
          break;
        case 40: // down arrow
          setSelectedElement(Math.min(hits.length - 1, selectedElement + 1));
          break;
        case 78: // ctrl n
          if (key.ctrlKey) {
            setSelectedElement(Math.min(hits.length - 1, selectedElement + 1));
          }
          break;
        case 80: // ctrl p
          if (key.ctrlKey) {
            setSelectedElement(Math.max(0, selectedElement - 1));
          }
          break;
        case 13: // Enter
          if (selectedURL != null && !isNavigating) {
            key.preventDefault();
            // Blur the search input immediately before navigation
            const searchInput = document.getElementById('search-input');
            if (searchInput instanceof HTMLElement) {
              searchInput.blur();
            }
            setIsNavigating(true);
            closeSearch();
            router.push(selectedURL);
          }
          break;
        default:
          if (!key.ctrlKey) {
            setSelectedElement(0);
          }
      }
    },
    [
      router,
      selectedElement,
      selectedURL,
      hits.length,
      isNavigating,
      closeSearch,
    ],
  );

  useEffect(() => {
    window.addEventListener('keydown', onEvent);
    return () => {
      window.removeEventListener('keydown', onEvent);
    };
  }, [onEvent]);

  const showingCount = hits.length;
  const hasMore = totalResults > showingCount;

  const handleNavigate = useCallback(
    (url: string) => {
      if (!isNavigating) {
        setIsNavigating(true);
        closeSearch();
        router.push(url);
      }
    },
    [isNavigating, router, closeSearch],
  );

  return (
    <div
      className={cx(isNavigating && styles.navigatingOut)}
      data-navigating={isNavigating}
    >
      <div className={styles.resultsCount}>
        {hasMore
          ? `Showing ${showingCount} of ${totalResults} results`
          : `${totalResults} result${totalResults === 1 ? '' : 's'}`}
      </div>
      <ul className={styles.listContainer}>
        {hits.map((hit, index) => {
          return (
            <li
              role='option'
              aria-selected={index === selectedElement}
              key={index}
            >
              <SearchHit
                hit={hit}
                isSelected={index === selectedElement}
                setURL={setSelectedURL}
                sendEvent={sendEvent}
                onNavigate={handleNavigate}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

function connectHits(Component: React.FC<HitsPropsWithTotal>) {
  const HitsWrapper: React.FC<{ query: string }> = (props) => {
    const { hits, sendEvent, results } = useHits<HitFields>();
    const totalResults = results?.nbHits ?? hits.length;
    return (
      <Component
        {...props}
        hits={hits}
        sendEvent={sendEvent}
        totalResults={totalResults}
      />
    );
  };
  return HitsWrapper;
}

const CustomHits = connectHits(Hits);

const SearchResults: React.FC = () => {
  const { indexUiState, results } = useInstantSearch();

  if (!indexUiState.query) {
    return <EmptyState />;
  }

  // Check if results are for the current query (prevents flash of "No results")
  const resultsMatchQuery = results?.query === indexUiState.query;

  if (results && results.nbHits !== 0) {
    return <CustomHits query={indexUiState.query} />;
  }

  // Only show "No results" when results are for the current query
  if (resultsMatchQuery) {
    return <NoResults query={indexUiState.query} />;
  }

  return null;
};

SearchResults.displayName = 'CoreExchangeSearchResults';

export default SearchResults;
